import { Base, Component, Emit, Prop } from '@/vue-property-decorator'
import { CommonLoading } from '@/components/business-components'
import { GlobalConst } from '@/context'
import { GoogleHelper, thirdPartyLogin } from '@/controller/ThirdPartyLogin'
import { PngSprite } from '@/components/icon-sprite'
import { ThirdWayStatus } from '@/api/common/type'
import { ThirdWayType } from '@/controller/ThirdPartyLogin/types'
import { createPngSprite, createSvgSprite } from '@/utils/business-utils/assets'
import { isWgPackage, windowConfig } from '@/utils/window'
import { useI18n } from '@/i18n'
import { useMainStore } from '@/store/index'
import AppTypes, { array } from '@/vue-types'
import Skin from '@/controller/Skin'
import style from './style.module.scss'

type Events = {
  onComplete: string
}

export type Props = {
  previewMode?: boolean
  showLabel?: boolean
  hiddenShadow?: boolean
  businessMode?: BusinessMode
  threeWayConfig: ThirdWayType[]
  hasBind?: boolean
  type?: 'login' | 'register'
  align?: 'center' | 'start'
}

export enum BusinessMode {
  /** 第三方登录 */
  LOGIN_REGISTER,
  /** 安全中心第三方绑定 */
  BIND
}

type LoginInfos = {
  email?: string
  access_token: string
  id?: string
}
type State = {
  loading: Record<ThirdWayType, boolean>
}

@Component<ThreeWayBind>({ name: 'ThreeWayBind' })
export default class ThreeWayBind extends Base<State, Props, Events> {
  @Prop(AppTypes.looseBool.def(false))
  private previewMode!: NonNullable<Props['previewMode']>
  @Prop(AppTypes.looseBool.def(false))
  private showLabel!: NonNullable<Props['showLabel']>

  @Prop(AppTypes.looseBool.def(false))
  private hiddenShadow!: NonNullable<Props['hiddenShadow']>

  @Prop(AppTypes.string.def('center'))
  private align!: Props['align']

  @Prop({
    default: BusinessMode.LOGIN_REGISTER
  })
  private businessMode!: NonNullable<Props['businessMode']>
  private loginLineResolve: ((value: LoginInfos) => void) | null = null

  @Prop(
    array<ThirdWayType>().def(() => [
      ThirdWayType.FACEBOOK,
      ThirdWayType.GOOGLE
    ])
  )
  private threeWayConfig!: Props['threeWayConfig']

  @Prop(AppTypes.string.def('login'))
  private type!: Props['type']

  @Prop(AppTypes.looseBool.def(false))
  private hasBind!: NonNullable<Props['hasBind']>
  state: State = {
    loading: {
      [ThirdWayType.FACEBOOK]: false,
      [ThirdWayType.GOOGLE]: false,
      [ThirdWayType.WECHAT]: false,
      [ThirdWayType.QQ]: false,
      [ThirdWayType.LINE]: false
    }
  }

  private get threeWayMap() {
    return {
      [ThirdWayType.FACEBOOK]: {
        name: 'Facebook',
        sprite: createPngSprite('sprite_main_img_login_fb'),
        type: ThirdWayStatus.FACEBOOK
      },
      [ThirdWayType.GOOGLE]: {
        name: 'Google',
        sprite: createPngSprite('sprite_main_img_login_google'),
        type: ThirdWayStatus.GOOGLE
      },
      [ThirdWayType.WECHAT]: {
        name: 'Wechat',
        sprite: createPngSprite('sprite_main_img_login_wx'),
        type: ThirdWayStatus.WECHAT
      },
      [ThirdWayType.QQ]: {
        name: 'QQ',
        sprite: createPngSprite('sprite_main_img_login_qq'),
        type: ThirdWayStatus.QQ
      },
      [ThirdWayType.LINE]: {
        name: 'Line',
        sprite: createPngSprite('sprite_main_img_login_line'),
        type: ThirdWayStatus.LINE
      }
    } as Record<
      ThirdWayType,
      {
        sprite: PngSprite
        name: string
        type: ThirdWayStatus
      }
    >
  }

  private async onLogin(mode: ThirdWayType) {
    thirdPartyLogin.handleClick(
      mode,
      () => {
        this.state.loading[mode] = true
      },
      () => {
        this.state.loading[mode] = false
      }
    )
  }

  @Emit('complete')
  private async loginCallBack(mode: ThirdWayType, loginInfos: LoginInfos) {
    this.state.loading[mode] = true
    await thirdPartyLogin.handleThirdPartyUserInfo(mode, loginInfos)
    this.state.loading[mode] = false
  }

  created() {
    GoogleHelper.initSDK()
  }

  /**
   * 需要隐藏三方登录的场景
   */
  get hiddenThirdLogin() {
    return isWgPackage && !windowConfig.speedPackageConfig
  }

  render() {
    const { t } = useI18n()
    const { isWeb } = useMainStore()
    const { isGalaxyGold, isCrownGold } = Skin.currentTheme()

    // 如果是马甲包(非极速包)就不显示第三方登录
    if (this.hiddenThirdLogin) return <template></template>
    return (
      <div class={[style.main]}>
        {this.$slots.title ||
          (this.businessMode === BusinessMode.LOGIN_REGISTER ? (
            <div
              class={[
                style.title,
                GlobalConst.CssClassName.Lobby.Color.TextLighten,
                'textLighten'
              ]}
            >
              {t('lobby.modal.login.otherWayLogin')}
            </div>
          ) : null)}
        <div
          class={{
            [style.content]: true,
            [style.previewMode]: this.previewMode,
            [style.hiddenShadow]: this.hiddenShadow
          }}
          data-business-mode={this.businessMode}
          data-align={this.align}
        >
          {this.threeWayConfig.map((type, index) => {
            const config = this.threeWayMap[type]
            // const loginWidth = isWeb ? 56 : 70
            const loginWidth = isWeb
              ? isGalaxyGold || isCrownGold
                ? 40
                : 56
              : 70
            const bindWidth = isWeb ? 60 : 80
            const scale =
              this.businessMode === BusinessMode.LOGIN_REGISTER
                ? loginWidth / config.sprite.width
                : bindWidth / config.sprite.width
            return (
              <div class={[style.item, `three-wan-bind__${type}`]} key={index}>
                <CommonLoading spinning={this.state.loading[type]}>
                  <icon-sprite
                    nativeOn={{
                      click: () => {
                        if (this.previewMode) return
                        this.onLogin(type)
                      }
                    }}
                    class={style.sprite}
                    sprite={config.sprite}
                    scale={scale}
                  />
                  {/* 绑定第三方账号成功时展示 */}
                  {this.hasBind && (
                    <icon-sprite
                      class={style['has-bind']}
                      sprite={createSvgSprite('comm_icon_gou')}
                    ></icon-sprite>
                  )}
                </CommonLoading>
                <span v-show={this.showLabel}>{config.name}</span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }
}
